// autopilot-ops-checks-cli-wiring.ts
//
// Shared CLI-layer helpers for `autopilot run`/`autopilot doctor`
// (code-reviewer-260909-p8-m4.md H1/H2 follow-up): building a real
// `opsChecks` deps object (config + probeClaudeVersion + REAL
// openAttemptIds — never the fail-open `?? []` default) and journaling a
// §18 beforeDispatch block as mission status. Split out of
// commands/autopilot.ts purely to keep that file ≤200 lines (same reason
// autopilot-control-directive-commands.ts was split in P5).
import { randomUUID } from "node:crypto";
import type { RunOpsDoctorChecksInput } from "./autopilot-doctor-ops-checks.js";
// N9 (phase 05): the probe now lives in the neutral claude-version-probe.ts
// (no CLI-wiring dependency) — re-exported here so the existing importer
// (mission-initializer-pin-baseline.ts) keeps compiling unchanged.
import { probeClaudeVersion } from "./claude-version-probe.js";
import { type EffectiveConfigLoadResult, loadEffectiveConfig } from "./effective-config-loader.js";
import { MissionStore } from "./mission-store.js";
import { type BeforeDispatchDecision, readOpenAttemptIds } from "./supervisor-dispatch-guards.js";

export { probeClaudeVersion };

/**
 * Loads effective-config and builds `opsChecks` for `runAutopilotDoctorChecks`
 * with a REAL `openAttemptIds` read — `undefined` when effective-config
 * failed to load (opsChecks stays opt-in then, same as pre-init doctor calls).
 * Used by both `autopilot doctor` and (indirectly, via startSupervisor) `run`.
 */
export async function loadOpsChecksDeps(
  globalConfigPath: string,
  controlRoot: string,
): Promise<{
  effectiveConfig: EffectiveConfigLoadResult;
  opsChecks: Omit<RunOpsDoctorChecksInput, "missionWorkspace" | "controlRoot"> | undefined;
}> {
  const effectiveConfig = await loadEffectiveConfig({ globalConfigPath, controlRoot });
  if (!effectiveConfig.ok) return { effectiveConfig, opsChecks: undefined };
  const openAttemptIds = await readOpenAttemptIds(controlRoot);
  return {
    effectiveConfig,
    opsChecks: { config: effectiveConfig.config, probeClaudeVersion, openAttemptIds },
  };
}

/**
 * `autopilot run`'s §18 gate handling: journals a beforeDispatch block as
 * mission status (`mission_status_changed{status:"blocked:<code>"}`) so
 * `autopilot status` reports WHY no worker dispatched this run, then logs
 * and releases the lock. Returns `true` when the caller should exit(1)
 * without proceeding — no-op (returns `false`) when not blocked.
 */
export async function handleBeforeDispatchBlockForRunCommand(
  controlRoot: string,
  decision: BeforeDispatchDecision | undefined,
  deps: { log: { error: (msg: string) => void }; releaseLock: () => Promise<void> },
): Promise<boolean> {
  if (!decision?.blocked) return false;
  const store = await MissionStore.open(controlRoot);
  try {
    await store.commit([
      {
        event_id: randomUUID(),
        delta: {
          kind: "mission_status_changed",
          status: `blocked:${decision.code ?? "before_dispatch"}`,
        },
      },
    ]);
  } finally {
    await store.close();
  }
  deps.log.error(`Dispatch bị chặn (§18): ${decision.reason}`);
  await deps.releaseLock();
  return true;
}
